// src/components/CreatePostModal.tsx
import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

// TypeScript interfaces
interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPostCreated?: (post: PostResponse) => void;
}

interface PostResponse {
  success: boolean;
  post_id: string;
  media_url?: string;
  thumbnail_url?: string;
  message: string;
  error?: string;
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({ isOpen, onClose, onPostCreated }) => {
  const [content, setContent] = useState<string>('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<string>('none');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState<boolean>(false);

  // Crop-related state
  const [showCropper, setShowCropper] = useState<boolean>(false);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0 });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const maxContentLength = 500;

  // File validation
  const validateFile = (file: File): { isValid: boolean; error?: string; type?: string } => {
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const videoTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/mkv', 'video/webm'];

    if (imageTypes.includes(file.type)) {
      if (file.size > 10 * 1024 * 1024) {
        return { isValid: false, error: 'Image size must be less than 10MB' };
      }
      return { isValid: true, type: 'image' };
    } else if (videoTypes.includes(file.type)) {
      if (file.size > 100 * 1024 * 1024) {
        return { isValid: false, error: 'Video size must be less than 100MB' };
      }
      return { isValid: true, type: 'video' };
    } else {
      return { isValid: false, error: 'Unsupported file type. Please use images or videos.' };
    }
  };

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    const validation = validateFile(file);

    if (!validation.isValid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    setError('');
    setMediaFile(file);
    setMediaType(validation.type || 'none');

    const previewUrl = URL.createObjectURL(file);
    setMediaPreview(previewUrl);

    // Reset crop states
    setCroppedImageUrl(null);
    setCrop(undefined);
    setCompletedCrop(undefined);

    // Show cropper for images
    if (validation.type === 'image') {
      setShowCropper(true);
    }
  }, []);

  // Handle image load for cropper
  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setImgDimensions({ width, height });

    // Start with no crop (show full image) - user can select format if desired
    setCrop(undefined);
    setZoom(1);
  };

  // Generate cropped image
  const generateCroppedImage = useCallback(async () => {
    if (!completedCrop || !imageRef.current || !canvasRef.current) return;

    const image = imageRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // ReactCrop gives us pixel coordinates relative to the displayed image element
    // We need to convert these to coordinates relative to the natural image
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Convert crop coordinates to natural image coordinates
    const sourceX = completedCrop.x * scaleX;
    const sourceY = completedCrop.y * scaleY;
    const sourceWidth = completedCrop.width * scaleX;
    const sourceHeight = completedCrop.height * scaleY;

    // Set canvas size to the crop size (this determines output resolution)
    canvas.width = sourceWidth;
    canvas.height = sourceHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw the cropped portion from the natural image
    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      sourceWidth,
      sourceHeight
    );

    return new Promise<string>((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const croppedUrl = URL.createObjectURL(blob);
          setCroppedImageUrl(croppedUrl);
          resolve(croppedUrl);
        }
      }, 'image/jpeg', 0.95);
    });
  }, [completedCrop]);

  // Apply crop
  const applyCrop = async () => {
    if (completedCrop) {
      await generateCroppedImage();
      setShowCropper(false);
    }
  };

  // Convert canvas to file for upload
  const canvasToFile = (canvas: HTMLCanvasElement, fileName: string): Promise<File> => {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], fileName, { type: 'image/jpeg' });
          resolve(file);
        }
      }, 'image/jpeg', 0.9);
    });
  };

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Handle drag and drop
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  // Remove media
  const removeMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType('none');
    setShowCropper(false);
    setCroppedImageUrl(null);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setZoom(1);
    setImgDimensions({ width: 0, height: 0 });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview);
    }
    if (croppedImageUrl) {
      URL.revokeObjectURL(croppedImageUrl);
    }
  };

  // Reset form
  const resetForm = () => {
    setContent('');
    removeMedia();
    setError('');
    setIsLoading(false);
  };

  // Handle modal close
  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onClose();
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!content.trim() && !mediaFile) {
      setError('Please enter content or select a media file');
      return;
    }

    if (content.length > maxContentLength) {
      setError(`Content must be less than ${maxContentLength} characters`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

      if (!token) {
        throw new Error('Authentication required');
      }

      const formData = new FormData();
      formData.append('content', content.trim());

      if (mediaFile) {
        let fileToUpload = mediaFile;

        // If image was cropped, use the cropped version
        if (mediaType === 'image' && completedCrop && canvasRef.current) {
          fileToUpload = await canvasToFile(canvasRef.current, `cropped_${mediaFile.name}`);
        }

        formData.append('media_file', fileToUpload);
        formData.append('media_type', mediaType);
      }

      const response = await fetch('https://aztrainersbackend.onrender.com/api/posts/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        resetForm();
        onClose();
        if (onPostCreated) {
          onPostCreated(result);
        }
      } else {
        setError(result.error || 'Failed to create post');
      }

    } catch (error) {
      console.error('Error creating post:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Character count styling
  const getCharCountClass = () => {
    const remaining = maxContentLength - content.length;
    if (remaining < 20) return 'text-red-500';
    if (remaining < 50) return 'text-yellow-500';
    return 'text-gray-500';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {showCropper ? 'Crop Image' : 'Create Post'}
          </h2>
          <button
            onClick={showCropper ? () => setShowCropper(false) : handleClose}
            disabled={isLoading}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Image Cropper */}
        {showCropper && mediaPreview && mediaType === 'image' && (
          <div className="p-6">
            {/* Zoom Controls */}
            <div className="mb-4 bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Zoom</span>
                <span className="text-sm text-gray-500">{Math.round(zoom * 100)}%</span>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
                  className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-50 text-gray-600 font-bold"
                  disabled={zoom <= 0.5}
                >
                  −
                </button>
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <button
                  onClick={() => setZoom(Math.min(3, zoom + 0.1))}
                  className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-50 text-gray-600 font-bold"
                  disabled={zoom >= 3}
                >
                  +
                </button>
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Fit all</span>
                <span>Zoom in</span>
              </div>
            </div>

            {/* Crop Area */}
            <div className="mb-4 bg-gray-100 rounded-lg overflow-hidden">
              <div className="flex justify-center items-center min-h-[300px] max-h-[500px] overflow-hidden">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1} // Square crop - remove this line for free-form cropping
                  minWidth={50}
                  minHeight={50}
                  className="max-w-full max-h-full"
                >
                  <img
                    ref={imageRef}
                    src={mediaPreview}
                    alt="Crop preview"
                    onLoad={onImageLoad}
                    style={{
                      transform: `scale(${zoom})`,
                      transformOrigin: 'center center',
                      maxWidth: '100%',
                      maxHeight: '450px',
                      objectFit: 'contain'
                    }}
                  />
                </ReactCrop>
              </div>
            </div>

            <div className="text-center space-y-3">
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <span>📱</span>
                  <span>Drag to move crop area</span>
                </div>
                <div className="flex items-center space-x-1">
                  <span>🔍</span>
                  <span>Use zoom to fit entire image</span>
                </div>
              </div>
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={() => {
                    setShowCropper(false);
                    setCrop(undefined);
                    setCompletedCrop(undefined);
                    setZoom(1);
                  }}
                  className="px-6 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setZoom(1)}
                  className="px-4 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  Reset Zoom
                </button>
                <button
                  onClick={applyCrop}
                  disabled={!completedCrop}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Apply Crop
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Regular Form Content */}
        {!showCropper && (
          <div className="p-6">
            {/* Content Input */}
            <div className="mb-6">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="What's on your mind?"
                className="w-full p-4 border-2 border-gray-200 rounded-xl resize-none focus:border-blue-500 focus:outline-none transition-colors"
                rows={4}
                maxLength={maxContentLength}
                disabled={isLoading}
              />
              <div className={`text-sm text-right mt-2 ${getCharCountClass()}`}>
                {content.length}/{maxContentLength}
              </div>
            </div>

            {/* File Upload Area - only show if no media selected */}
            {!mediaPreview && (
              <div className="mb-6">
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${isDragOver
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*,video/*"
                    className="hidden"
                    disabled={isLoading}
                  />

                  <div className="flex flex-col items-center">
                    <div className="text-4xl text-gray-400 mb-2">📁</div>
                    <p className="text-gray-600 font-medium">Add photos or videos</p>
                    <p className="text-gray-400 text-sm mt-1">
                      Drag and drop or click to select
                    </p>
                    <p className="text-gray-400 text-xs mt-2">
                      Images: max 10MB | Videos: max 100MB
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Media Preview */}
            {mediaPreview && !showCropper && (
              <div className="mb-6 relative bg-gray-100 rounded-xl overflow-hidden">
                {mediaType === 'image' ? (
                  <div>
                    <img
                      src={croppedImageUrl || mediaPreview}
                      alt="Preview"
                      className="w-full h-auto max-h-96 object-cover"
                    />
                    <div className="absolute bottom-3 left-3 flex space-x-2">
                      <button
                        onClick={() => setShowCropper(true)}
                        className="bg-white text-gray-800 px-3 py-1 rounded-lg text-sm hover:bg-gray-100 transition-colors"
                      >
                        {croppedImageUrl ? 'Re-crop' : 'Crop'}
                      </button>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-white text-gray-800 px-3 py-1 rounded-lg text-sm hover:bg-gray-100 transition-colors"
                      >
                        Replace
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <video
                      src={mediaPreview}
                      controls
                      className="w-full h-auto max-h-96"
                      muted
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-3 left-3 bg-white text-gray-800 px-3 py-1 rounded-lg text-sm hover:bg-gray-100 transition-colors"
                    >
                      Replace
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={removeMedia}
                  className="absolute top-3 right-3 bg-red-500 text-white w-8 h-8 rounded-full hover:bg-red-600 transition-colors flex items-center justify-center text-lg font-bold"
                  disabled={isLoading}
                >
                  ×
                </button>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || (!content.trim() && !mediaFile)}
                className="px-6 py-3 bg-gradient-to-r from-[#E6A756] to-[#D2824C] text-[#5D4037] rounded-xl hover:from-#D2824C-600 hover:to-#5D4037-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <span>Create Post</span>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Hidden canvas for image processing */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

export default CreatePostModal;